// The server/rules own authorization. This controller keeps listeners from
// starting before renewal and restarts the app after a lease lapses. Use a
// monotonic AND wall-clock deadlines, starting BEFORE the network request.
// performance.now can pause during device sleep (WebKit #225610), while the
// server's lease keeps expiring. The earlier deadline catches that sleep without
// letting a wall-clock rollback lengthen the monotonic window.
// The api's minute sweep deactivates a lease this long before its deadline
// (SESSION_ACCESS_EXPIRY_LEAD_MS in api/src/data/sessionAccessExpiry.js), so a
// phone that sleeps past renewal wakes with listeners the rules already deny.
// Treat the lease as lapsed from that point so they close before reconnecting.
export const SESSION_ACCESS_EXPIRY_LEAD_MS = 60000;

// The running controller's lease, for listener error handlers. On wake the Watch
// stream reconnects and every protected listener is denied in the same instant,
// before the gate has unmounted them, so a denial while this reports lapsed is
// the gate's to recover (by remounting), not a rules failure to report. The
// client deadline is computed from before the renewal request and minus the
// server's sweep lead, so it lapses no later than the server starts denying.
let activeLease = null;
export const isAccessLeaseLapsed = () => Boolean(activeLease?.lapsed());

// Protected file fetches aren't listeners the gate can unmount first. A preview
// refresh timer that comes due while the device sleeps fires on wake, before
// the gate has seen the lapse, and the api refuses every one: a laptop showing
// 34 member avatars logged 34 403s per wake. They wait here instead and go out
// once renewal succeeds, which is also when the gate reopens the app, so the
// remounted avatars pick up the same in-flight requests.
let leaseWaiters = new Set();
const releaseLeaseWaiters = () => {
    const waiters = leaseWaiters;
    leaseWaiters = new Set();
    waiters.forEach(release => release());
};

export const whenAccessLeaseReady = ({signal} = {}) => {
    if (!isAccessLeaseLapsed()) return Promise.resolve();
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(signal.reason);
            return;
        }
        const onAbort = () => {
            leaseWaiters.delete(release);
            reject(signal.reason);
        };
        const release = () => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        };
        leaseWaiters.add(release);
        signal?.addEventListener('abort', onAbort, {once: true});
    });
};

export const startSessionAccess = ({renew, onReady, onError, now = () => performance.now(), wallNow = () => Date.now()}) => {
    let stopped = false, pending = false, deadline = 0, wallDeadline = 0, renewalTimer, expiryTimer, requestTimer;
    let blocked = false, observed = false, validAfter;
    const remainingTime = () => Math.min(deadline - now(), wallDeadline - wallNow()) - SESSION_ACCESS_EXPIRY_LEAD_MS;
    const schedule = delay => {
        clearTimeout(renewalTimer);
        if (!stopped) renewalTimer = setTimeout(check, delay);
    };
    const check = async () => {
        if (stopped) return;
        if (remainingTime() <= 0) onReady(false);
        if (pending) return;
        pending = true;
        const started = now();
        const wallStarted = wallNow();
        try {
            const {expiresInMs} = await Promise.race([
                renew(),
                new Promise((_, reject) => {
                    requestTimer = setTimeout(() => reject(new Error('Connection timed out')), 30000);
                })
            ]);
            if (stopped) return;
            if (!Number.isFinite(expiresInMs) || expiresInMs <= 0 || expiresInMs > 300000) {
                throw new Error('Invalid access renewal response');
            }
            deadline = started + expiresInMs;
            wallDeadline = wallStarted + expiresInMs;
            const remaining = remainingTime();
            if (remaining <= 0) throw new Error('Access check took too long');
            clearTimeout(expiryTimer);
            expiryTimer = setTimeout(() => {
                if (!stopped) { onReady(false); check(); }
            }, remaining);
            onError(null);
            onReady(!blocked);
            if (!blocked) releaseLeaseWaiters();
            schedule(Math.min(120000, remaining / 2));
        } catch (error) {
            if (stopped) return;
            if (remainingTime() <= 0) onReady(false);
            onError(error);
            // A competing tab/account writer is expected to win occasionally.
            schedule((error.serverCode || error.code) === 'aborted' ? 1000 : 15000);
        } finally { clearTimeout(requestTimer); pending = false; }
    };
    const lease = {lapsed: () => blocked || remainingTime() <= 0};
    activeLease = lease;
    const wake = () => { if (document.visibilityState !== 'hidden') check(); };
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', wake);
    check();
    return {
        retry: check,
        observe: state => {
            if (stopped) return;
            const wasBlocked = blocked;
            blocked = state?.status !== 'ready' || state?.active !== true;
            const cutoffChanged = observed && state?.validAfter > validAfter;
            validAfter = state?.validAfter;
            if ((!observed && blocked) || wasBlocked !== blocked || cutoffChanged) {
                deadline = 0;
                wallDeadline = 0;
                onReady(false);
                check();
            }
            observed = true;
        },
        stop: () => {
            stopped = true;
            if (activeLease === lease) activeLease = null;
            // Signed out or switched account: let waiting fetches go and fail
            // on the file client's own session check.
            releaseLeaseWaiters();
            clearTimeout(renewalTimer);
            clearTimeout(expiryTimer);
            clearTimeout(requestTimer);
            window.removeEventListener('focus', wake);
            window.removeEventListener('online', wake);
            document.removeEventListener('visibilitychange', wake);
        }
    };
};
